// CSC-only WhatsApp proposal message builder.
// Extracted verbatim from commercialSolarCalculator.jsx (buildMsg) so both the
// old calculator and the Combined tab can share one source of truth.
// NO RSC imports — commercial (CSC) only.
import { sstOn } from '../../utils/sst';

const fmt   = n => Number(n).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtRM = n => 'RM ' + fmt(n);
// SST shown to 2 decimals (exact, not whole-ringgit) — user 2026-08-04.
const fmtRM2 = n => 'RM ' + Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function buildCscWhatsAppMsg(r, lang) {
    const hasBatt   = r.battKwh > 0;
    const battPart  = hasBatt ? ` + ${r.battKwh}kWh Battery` : '';
    const panelPart = r.panels ? ` (${r.panels} panel)` : '';
    // headerOverride lets callers (e.g. the multi-meter Combined message) supply a
    // breakdown-form header line — "*Combined* — (18 + 19) kWp · (16 + 32) kWh" —
    // while still reusing the rest of this builder verbatim.
    const header    = r.headerOverride != null ? r.headerOverride : `${r.kwp}kWp${panelPart}${battPart}`;
    const pct       = r.billSavingPct;
    const billStr   = r.bill > 0 ? ` (${fmtRM(Math.round(r.bill))})` : '';
    const co        = r.company ? r.company + '\n' : '';
    const hasDisc   = r.discount > 0;
    const isSare    = r.genMode === 'sare';

    const pkgBlock = label => {
        const isCN = lang === 'zh';
        const isMY = lang === 'ms';
        const lines = [];
        if (r.panels > 0) {
            if (isCN)       lines.push(`✅ ${r.panels}x Trina 全球Tier 1太阳能电板`);
            else if (isMY)  lines.push(`✅ ${r.panels}x Panel Solar Trina (Global Tier 1)`);
            else            lines.push(`✅ ${r.panels}x Trina Solar Panels (Global Tier 1)`);
        }
        if (r.kw) lines.push(`✅ Inverter = ${r.kw}kW`);
        if (hasBatt) {
            if (isCN)       lines.push(`✅ ${r.battKwh}kWh Battery`);
            else if (isMY)  lines.push(`✅ ${r.battKwh}kWh Bateri`);
            else            lines.push(`✅ ${r.battKwh}kWh Battery`);
        }
        // Catalogue add-ons the agent added (optimizers, etc.) — listed as package items.
        (r.addonItems || []).forEach(a => {
            const nm = (a.name || '').trim();
            if (!nm) return;
            const q = Number(a.qty) || 1;
            lines.push(`✅ ${q > 1 ? q + 'x ' : ''}${nm}`);
        });
        if (isCN)       lines.push(isSare ? `✅ 0 安装费 & 包括A-Z政府申请手续` : `✅ 包括安装费 & A-Z政府申请手续`);
        else if (isMY)  lines.push(isSare ? `✅ Pemasangan PERCUMA & Permohonan Kerajaan A-Z` : `✅ Pemasangan & Permohonan Kerajaan A-Z`);
        else            lines.push(isSare ? `✅ FREE Installation & A-Z Government Application` : `✅ Installation & A-Z Government Application`);
        if (isCN)       lines.push(`✅ 10年 全球Tier 1 Inverter保修`);
        else if (isMY)  lines.push(`✅ Waranti 10 tahun Inverter (Global Tier 1)`);
        else            lines.push(`✅ 10-year Global Tier 1 Inverter Warranty`);
        if (hasBatt) {
            if (isCN)       lines.push(`✅ 10年 Battery保修`);
            else if (isMY)  lines.push(`✅ Waranti 10 tahun Bateri`);
            else            lines.push(`✅ 10-year Battery Warranty`);
        }
        if (isCN)       lines.push(`✅ 15年 Trina 全球Tier 1太阳能电板保修`);
        else if (isMY)  lines.push(`✅ Waranti 15 tahun Panel Solar Trina (Global Tier 1)`);
        else            lines.push(`✅ 15-year Trina Solar Panel Warranty (Global Tier 1)`);
        if (isCN)       lines.push(`✅ 30年 太阳能电板发电功效`);
        else if (isMY)  lines.push(`✅ Jaminan Prestasi 30 tahun Panel Solar`);
        else            lines.push(`✅ 30-year Solar Panel Performance Guarantee`);
        // Free O&M warranty years follow the CSC "Operation Warranty" dropdown (1 or 3);
        // SARE keeps its 20-yr lease term. One-time Cleaning appended for non-SARE. (2026-08-02)
        const yrs = isSare ? 20 : (Number(r.omWarrantyYears) || 1);
        /* The clean is named only when the warranty HAS one. It used to be appended to every
           non-SARE quote, so a system under the 30 kWp threshold - whose warranty reads
           plainly "1 Year" - was sent a WhatsApp promising a free clean it does not include.
           r.omCleaning comes from the chosen warranty, not from kWp, so a quote keeps what it
           was sold with. (user 2026-10-26) */
        const clean = (isSare || !r.omCleaning) ? ''
            : (isCN ? '（一次清洗）' : isMY ? ' (Pembersihan Sekali)' : ' (One-time Cleaning)');
        if (isCN)       lines.push(`✅ ${yrs}年Maintenance保修${clean}`);
        else if (isMY)  lines.push(`✅ Waranti Penyelenggaraan ${yrs} tahun${clean}`);
        else            lines.push(`✅ ${yrs}-year Maintenance Warranty${clean}`);
        return `\n--------------------------------\n💼 【 ${label} 】\n${lines.join('\n')}`;
    };

    const costBlock = (capLabel, discLabel, nettLabel) =>
        hasDisc
            ? `⚡ ${capLabel} = ~${fmtRM(Math.round(r.capitalCostRaw))}~\n⚡ ${discLabel} = -${fmtRM(Math.round(r.discount))}\n⚡ *${nettLabel} = ${fmtRM(Math.round(r.capitalCost))}*`
            : `⚡ ${capLabel} = ${fmtRM(Math.round(r.capitalCost))}`;

    // SST = 6% charged on 20% of the capital cost — via the single canonical helper
    // (utils/sst.js). Shown under Capital Cost on the owner proposal.
    const sstAmount = sstOn(r.capitalCost);
    const totalInclSst = (Number(r.capitalCost) || 0) + sstAmount;
    const sstLine = { en: `⚡ SST (6% on 20%) = ${fmtRM2(sstAmount)}\n⚡ *Total Price (incl SST) = ${fmtRM2(totalInclSst)}*`,
                      zh: `⚡ SST (20%的6%) = ${fmtRM2(sstAmount)}\n⚡ *总价（含SST）= ${fmtRM2(totalInclSst)}*`,
                      ms: `⚡ SST (6% atas 20%) = ${fmtRM2(sstAmount)}\n⚡ *Jumlah Harga (termasuk SST) = ${fmtRM2(totalInclSst)}*` };
    // Maintenance interval — systems < 96 kWp are serviced once every 3 years (per system,
    // not combined). The caller sets maintenancePeriodYears; default 1 (yearly).
    const maintYrs = Number(r.maintenancePeriodYears) || 1;
    const maintPer = { en: maintYrs > 1 ? `${maintYrs} years` : 'year',
                       zh: maintYrs > 1 ? `${maintYrs}年` : '年',
                       ms: maintYrs > 1 ? `${maintYrs} tahun` : 'tahun' };
    // SARE (Zero-Capex) monthly-saving line, shown BELOW the "With CA" block on the Outright
    // message so the customer sees the SARE alternative too. Only when a SARE saving exists.
    // (user 2026-07-22)
    // SARE saving is not one number: the TNB tariff escalates ~8% every 3 years while the
    // SARE rate stays flat, so the monthly saving GROWS across the 20-year term. Showing
    // only year 1 understates every later year. Quote it as a range, lowest to highest,
    // when the caller supplies min/max; fall back to the single figure otherwise so any
    // caller that does not is unaffected. (user 2026-08-21)
    // YEARLY, not monthly (user 2026-08-28). The annual figures come through the record
    // computed from the annual cash flow — never monthly x 12, which would print a
    // ringgit under the proposal card for the same lease.
    const _sMin = Math.round(Number(r.sareSavingYrMin) || 0);
    const _sMax = Math.round(Number(r.sareSavingYrMax) || 0);
    const _sHasRange = _sMin > 0 && _sMax > _sMin;
    const _sVal = {
        en: _sHasRange ? `${fmtRM(_sMin)} to ${fmtRM(_sMax)}` : fmtRM(Math.round(r.sareSavingYr)),
        zh: _sHasRange ? `${fmtRM(_sMin)} ~ ${fmtRM(_sMax)}` : fmtRM(Math.round(r.sareSavingYr)),
        ms: _sHasRange ? `${fmtRM(_sMin)} hingga ${fmtRM(_sMax)}` : fmtRM(Math.round(r.sareSavingYr)),
    };
    // Same three figures for the 10-year lease (user 2026-08-25). It charges +8 sen/kWh
    // for half the commitment, so its monthly saving is lower — quoting both lets the
    // customer weigh term against saving instead of being shown only one option.
    const _tMin = Math.round(Number(r.sare10SavingYrMin) || 0);
    const _tMax = Math.round(Number(r.sare10SavingYrMax) || 0);
    const _tHasRange = _tMin > 0 && _tMax > _tMin;
    const _tVal = {
        en: _tHasRange ? `${fmtRM(_tMin)} to ${fmtRM(_tMax)}` : fmtRM(Math.round(r.sare10SavingYr)),
        zh: _tHasRange ? `${fmtRM(_tMin)} ~ ${fmtRM(_tMax)}` : fmtRM(Math.round(r.sare10SavingYr)),
        ms: _tHasRange ? `${fmtRM(_tMin)} hingga ${fmtRM(_tMax)}` : fmtRM(Math.round(r.sare10SavingYr)),
    };
    // Loan block (user 2026-09-29). Two lines only - how long, and what it costs a
    // month - because that is the pair a customer asks for. Printed only when a loan
    // is actually part of the proposal AND the monthly figure resolved; a "RM 0/month
    // over 0 years" line is worse than no line.
    const _loanMo = Math.round(Number(r.loanMonthlyPmt) || 0);
    const _loanYrs = (Number(r.loanTermMonths) || 0) / 12;
    const _hasLoan = r.includeLoan !== false && _loanMo > 0 && _loanYrs > 0;
    const _loanYrsTxt = Number.isInteger(_loanYrs) ? String(_loanYrs) : _loanYrs.toFixed(1);
    const loanLine = _hasLoan
        ? {
            en: `\n\nEstimated loan repayment:`
                + `\n\u26a1 Loan Term = ${_loanYrsTxt} years`
                + `\n\u26a1 *Monthly Repayment = ${fmtRM(_loanMo)}*`,
            zh: `\n\n\u9884\u4f30\u8d37\u6b3e\u507f\u8fd8\uff1a`
                + `\n\u26a1 \u8d37\u6b3e\u5e74\u9650 = ${_loanYrsTxt} \u5e74`
                + `\n\u26a1 *\u6bcf\u6708\u507f\u8fd8 = ${fmtRM(_loanMo)}*`,
            ms: `\n\nAnggaran bayaran balik pinjaman:`
                + `\n\u26a1 Tempoh Pinjaman = ${_loanYrsTxt} tahun`
                + `\n\u26a1 *Bayaran Bulanan = ${fmtRM(_loanMo)}*`,
          }
        : { en: '', zh: '', ms: '' };

    const _has20 = Number(r.sareSavingMo) > 0;
    const _has10 = Number(r.sare10SavingMo) > 0;
    // Layout per user 2026-08-28: the TERM is the heading and the yearly saving sits under
    // it, so each lease reads as its own block. Previously a single "Monthly Saving"
    // heading carried both terms as bolt lines. Each term still labels itself, so the
    // reader never has to guess which lease a figure belongs to.
    const sareLine = (_has20 || _has10)
        ? {
            en: `\n\nSARE Program (ZERO Capex)`
                + (_has20 ? `\n20-Year terms\n⚡ *Yearly Saving = ${_sVal.en}*` : '')
                + (_has10 ? `\n10-Year terms\n⚡ *Yearly Saving = ${_tVal.en}*` : ''),
            zh: `\n\nSARE 零资本计划`
                + (_has20 ? `\n20 年方案\n⚡ *每年节省 = ${_sVal.zh}*` : '')
                + (_has10 ? `\n10 年方案\n⚡ *每年节省 = ${_tVal.zh}*` : ''),
            ms: `\n\nProgram SARE Tanpa Modal`
                + (_has20 ? `\nTerma 20 Tahun\n⚡ *Penjimatan Tahunan = ${_sVal.ms}*` : '')
                + (_has10 ? `\nTerma 10 Tahun\n⚡ *Penjimatan Tahunan = ${_tVal.ms}*` : ''),
          }
        : { en: '', zh: '', ms: '' };

    if (lang === 'en') {
        if (isSare) return (
`${co}*Commercial Proposal*
${header}
SARE Program Zero Capex
⚡ 20Years Contract
⚡ After 20years, solar system is YOURS
⚡ Capital Cost = RM0
⚡ Maintenance = RM 0/year

⚡ SARE rate = ${r.sareRate}/kWh = ${fmtRM(Math.round(r.savingMo))}
⚡ *Total 10 Year Saving = ${fmtRM(Math.round(r.saving10))}*
⚡ *Total 20 Year Saving = ${fmtRM(Math.round(r.saving20))}*
${pkgBlock('Package Includes')}`);
        return (
`${co}*Commercial Proposal*
${header}
${costBlock('Capital Cost', 'Discount', 'Nett Cost')}
${sstLine.en}
⚡ Maintenance = ${fmtRM(r.maintenance)}/${maintPer.en}

⚡ *Monthly Saving = ${fmtRM(Math.round(r.savingMo))}*${pct ? `\nEstimate saving ${pct.toFixed(1)}% of bill${billStr}` : ''}
⚡ *Total 10 Year Saving = ${fmtRM(Math.round(r.saving10))}*
⚡ *Total 20 Year Saving = ${fmtRM(Math.round(r.saving20))}*

⚡ *Yearly Yield Rate (nett) = ${r.yieldRate.toFixed(2)}%/year*
⚡ *ROI = ${r.roi.toFixed(1)} year*

With CA & GITA incentives
⚡ *ROI = ${r.roiAfterTax.toFixed(1)} year*${loanLine.en}${sareLine.en}
${pkgBlock('Package Includes')}`);
    }

    if (lang === 'zh') {
        if (isSare) return (
`${co}*商业太阳能方案*
${header}
SARE 零资本计划
⚡ 20年合约
⚡ 20年后，太阳能系统归您所有
⚡ 资本成本 = RM0
⚡ 维护费用 = RM 0/年

⚡ SARE 费率 = ${r.sareRate}/kWh = ${fmtRM(Math.round(r.savingMo))}
⚡ *10年总节省 = ${fmtRM(Math.round(r.saving10))}*
⚡ *20年总节省 = ${fmtRM(Math.round(r.saving20))}*
${pkgBlock('配套包括')}`);
        return (
`${co}*商业太阳能方案*
${header}
${costBlock('资本成本', '折扣', '优惠后价格')}
${sstLine.zh}
⚡ 维护费用 = ${fmtRM(r.maintenance)}/${maintPer.zh}

⚡ *每月节省 = ${fmtRM(Math.round(r.savingMo))}*${pct ? `\n估计节省电费 ${pct.toFixed(1)}%${billStr}` : ''}
⚡ *10年总节省 = ${fmtRM(Math.round(r.saving10))}*
⚡ *20年总节省 = ${fmtRM(Math.round(r.saving20))}*

⚡ *年回报率（净值）= ${r.yieldRate.toFixed(2)}%/年*
⚡ *回本年限 = ${r.roi.toFixed(1)} 年*

含 CA & GITA 税务优惠
⚡ *回本年限 = ${r.roiAfterTax.toFixed(1)} 年*${loanLine.zh}${sareLine.zh}
${pkgBlock('配套包括')}`);
    }

    if (lang === 'ms') {
        if (isSare) return (
`${co}*Cadangan Solar Komersial*
${header}
Program SARE Tanpa Modal
⚡ Kontrak 20 Tahun
⚡ Selepas 20 tahun, sistem solar adalah MILIK ANDA
⚡ Kos Modal = RM0
⚡ Penyelenggaraan = RM 0/tahun

⚡ Kadar SARE = ${r.sareRate}/kWh = ${fmtRM(Math.round(r.savingMo))}
⚡ *Jumlah Penjimatan 10 Tahun = ${fmtRM(Math.round(r.saving10))}*
⚡ *Jumlah Penjimatan 20 Tahun = ${fmtRM(Math.round(r.saving20))}*
${pkgBlock('Pakej Termasuk')}`);
        return (
`${co}*Cadangan Solar Komersial*
${header}
${costBlock('Kos Modal', 'Diskaun', 'Kos Selepas Diskaun')}
${sstLine.ms}
⚡ Penyelenggaraan = ${fmtRM(r.maintenance)}/${maintPer.ms}

⚡ *Penjimatan Bulanan = ${fmtRM(Math.round(r.savingMo))}*${pct ? `\nAnggaran jimat ${pct.toFixed(1)}% daripada bil${billStr}` : ''}
⚡ *Jumlah Penjimatan 10 Tahun = ${fmtRM(Math.round(r.saving10))}*
⚡ *Jumlah Penjimatan 20 Tahun = ${fmtRM(Math.round(r.saving20))}*

⚡ *Kadar Hasil Tahunan (nett) = ${r.yieldRate.toFixed(2)}%/tahun*
⚡ *ROI = ${r.roi.toFixed(1)} tahun*

Dengan insentif CA & GITA
⚡ *ROI = ${r.roiAfterTax.toFixed(1)} tahun*${loanLine.ms}${sareLine.ms}
${pkgBlock('Pakej Termasuk')}`);
    }
}

// ───────────────────────────────────────────────────────────────────────────
// RSC-style (AI BESS 360) WhatsApp message, fed by CSC's own data object.
// Mirrors residentialSolarCalculatorV2.buildAtapStyleMsg LAYOUT only — NO RSC
// imports, NO residential promo block. `lang` = 'en' | 'zh' | 'ms' (same codes
// the ModeToggle and buildCscWhatsAppMsg use — NOT 'cn'/'my').
// ───────────────────────────────────────────────────────────────────────────

const num = (n) => Math.round(Number(n) || 0).toLocaleString();

// Pretty-case a brand key for display (goodwe → GoodWe, solis → Solis, …).
const brandDisp = (b) => {
    const k = String(b || 'goodwe').toLowerCase();
    if (k === 'goodwe') return 'GoodWe';
    if (k === 'solis') return 'Solis';
    if (k === 'huawei') return 'Huawei';
    return k.charAt(0).toUpperCase() + k.slice(1);
};

// The 配套包括 / Package Includes block (bullets only), built from d.
function cscIncludesBullets(d, isCN, isMY) {
    const brandLabel = brandDisp(d.brandLabel);
    const lines = [];
    if (isCN) lines.push(`✅ ${d.panels}x ${d.panelWatt}W Tier 1 太阳能电板`);
    else      lines.push(`✅ ${d.panels}x ${d.panelWatt}W Tier 1 Solar Panels`);
    lines.push(`✅ Inverter ${d.inverterKw}kW ${brandLabel}`);
    if (Number(d.battery) > 0) lines.push(`✅ Battery ${d.battery}kWh`);
    if (isCN) lines.push(`✅ 安装费 & 包括申请手续`);
    else      lines.push(`✅ Installation & application handling`);
    if (isCN) lines.push(`✅ 10年 ${brandLabel} Inverter 保修`);
    else      lines.push(`✅ 10-Year ${brandLabel} Inverter Warranty`);
    if (Number(d.battery) > 0) {
        if (isCN) lines.push(`✅ 10年 Battery 保修`);
        else      lines.push(`✅ 10-Year Battery Warranty`);
    }
    if (isCN) lines.push(`✅ 15年 太阳能电板保修`);
    else      lines.push(`✅ 15-Year Solar Panel Warranty`);
    if (isCN) lines.push(`✅ 30年 太阳能电板发电功效`);
    else      lines.push(`✅ 30-Year Panel Performance`);
    const omLabel = d.omPkg === 'basic' ? (isCN ? '基础' : 'Basic') : (isCN ? '高级' : 'Premium');
    if (isCN) lines.push(`✅ ${omLabel} O&M Maintenance 配套`);
    else      lines.push(`✅ ${omLabel} O&M Maintenance package`);
    return lines.join('\n');
}

// The per-option body (kWp/battery, savings, ROI, CC, Cash) — no includes block.
function cscOptionBody(d, isCN, isMY) {
    const battDesc = Number(d.battery) > 0
        ? `${d.battery}kWh Battery`
        : (isCN ? '无电池' : isMY ? 'Tiada Bateri' : 'No Battery');

    const ccLabel = isCN ? '36M CC 0% 分期' : isMY ? '36B CC 0% Ansuran' : '36M CC 0% Instalment';
    const cashLabel = isCN ? 'Cash (省更多)' : isMY ? 'Tunai (Jimat Lebih)' : 'Cash (Save More)';
    const discWord = isMY ? 'diskaun' : 'discount';

    let m = `🔆 *${d.kwp}kWp · ${battDesc}*\n`;
    if (isCN)      m += `💰 省电: RM ${num(d.savingMo)}++/月\n📈 回本ROI: ${d.roi} 年 & 回酬Return: ${d.ret}%\n\n`;
    else if (isMY) m += `💰 Penjimatan: RM ${num(d.savingMo)}++/bulan\n📈 ROI: ${d.roi} tahun & Pulangan: ${d.ret}%\n\n`;
    else           m += `💰 Est. Savings: RM ${num(d.savingMo)}++/month\n📈 ROI: ${d.roi} yrs & Return: ${d.ret}%\n\n`;

    m += Number(d.ccDisc) > 0
        ? `💳 ${ccLabel}: ~RM ${num(d.ccOri)}~ RM${num(d.ccFinal)} (-RM${num(d.ccDisc)} ${discWord})\n`
        : `💳 ${ccLabel}: RM ${num(d.ccOri)}\n`;
    m += Number(d.cashDisc) > 0
        ? `💵 ${cashLabel}: ~RM ${num(d.cashOri)}~ RM${num(d.cashFinal)} (-RM${num(d.cashDisc)} ${discWord})\n`
        : `💵 ${cashLabel}: RM ${num(d.cashOri)}\n`;
    return m;
}

export function buildCscBessStyleMsg(d, lang) {
    if (!d) return '';
    const isCN = lang === 'zh', isMY = lang === 'ms';
    const cname = (d.name || '').trim() ? `${(d.name || '').trim()} ` : '';

    let m = isCN ? `✨ *${cname}商业太阳能方案* ✨\n`
          : isMY ? `✨ *${cname}Cadangan Solar Komersial* ✨\n`
          : `✨ *${cname}Commercial Solar Proposal* ✨\n`;

    if (Number(d.usageKwh) > 0) {
        const billStr = Number(d.bill) > 0 ? `RM ${num(d.bill)} · ` : '';
        if (isCN)      m += `💸 Bill : ${billStr}${num(d.usageKwh)}kWh\n⚡ 日间自用 : ${d.selfPct}%\n`;
        else if (isMY) m += `💸 Bil : ${billStr}${num(d.usageKwh)}kWh\n⚡ Guna Siang : ${d.selfPct}%\n`;
        else           m += `💸 Bill : ${billStr}${num(d.usageKwh)}kWh\n⚡ Day-time self-use : ${d.selfPct}%\n`;
    }

    m += '\n' + cscOptionBody(d, isCN, isMY);

    const includesHeader = isCN ? '【 配套包括 】' : isMY ? '【 Pakej Termasuk 】' : '【 Package Includes 】';
    m += `\n--------------------------------\n📦 ${includesHeader}\n`;
    m += cscIncludesBullets(d, isCN, isMY);
    return m;
}

export function buildCscBessComboMsg(dA, dB, lang) {
    if (!dA || !dB) return '';
    const isCN = lang === 'zh', isMY = lang === 'ms';
    const cname = (dA.name || '').trim() ? `${(dA.name || '').trim()} ` : '';

    let m = isCN ? `✨ *${cname}商业太阳能 A 对比 B* ✨\n`
          : isMY ? `✨ *${cname}Solar Komersial — A vs B* ✨\n`
          : `✨ *${cname}Commercial Solar — A vs B* ✨\n`;

    if (Number(dA.usageKwh) > 0) {
        const billStr = Number(dA.bill) > 0 ? `RM ${num(dA.bill)} · ` : '';
        if (isCN)      m += `💸 Bill : ${billStr}${num(dA.usageKwh)}kWh\n⚡ 日间自用 : ${dA.selfPct}%\n`;
        else if (isMY) m += `💸 Bil : ${billStr}${num(dA.usageKwh)}kWh\n⚡ Guna Siang : ${dA.selfPct}%\n`;
        else           m += `💸 Bill : ${billStr}${num(dA.usageKwh)}kWh\n⚡ Day-time self-use : ${dA.selfPct}%\n`;
    }

    m += `\n*Option A*\n` + cscOptionBody(dA, isCN, isMY);
    m += `\n━━━━━━━━━━━━━━━━\n`;
    m += `*Option B*\n` + cscOptionBody(dB, isCN, isMY);

    const includesHeader = isCN ? '【 配套包括 】' : isMY ? '【 Pakej Termasuk 】' : '【 Package Includes 】';
    m += `\n--------------------------------\n📦 ${includesHeader}\n`;
    m += cscIncludesBullets(dA, isCN, isMY);
    return m;
}

// ── BESS peak-shaving summary (user 2026-09-26) ──────────────────────────────
// The solar message answers "how much of the bill does the sun take off". A BESS on an
// MV site answers a different question — how much of the MAXIMUM DEMAND charge it holds
// down — so it gets its own block rather than another line in the solar list. Figures
// come straight from the Part A card, so the chat, the card and the proposal agree.

export function buildCscBessMsg(pick, o = {}, lang = 'en') {
    if (!pick) return '';
    const isCN = lang === 'zh', isMY = lang === 'ms';
    const co = o.company ? `${o.company}\n` : '';
    const brand = o.brandLabel || 'GoodWe';
    const unitKwh = Number(o.unitKwh) || 261;
    const nameplate = pick.nameplateKwh || pick.units * unitKwh;
    const sst = sstOn(pick.capex || 0);
    // 10- and 20-year totals come off the SAME degraded cashflow the proposal prints, so the
    // chat and the PDF can never disagree. cashflow[n] is cumulative and starts at -capex.
    const cf = pick.cashflow || [];
    const cum = (y) => (cf[y] != null ? cf[y] + (pick.capex || 0) : (pick.annualYear1 || 0) * y);

    const T = isCN ? {
        head: '*BESS 削峰方案*', size: '电池容量', cost: '资本成本', sstL: 'SST (20%的6%)', total: '总价（含SST）',
        save: '每月节省', shave: '平均削减最高需量', s10: '10年总节省', s20: '20年总节省',
        roi: 'ROI', inc: '含 CA & GITA 奖掖', pkg: '配套包括',
        note: '削峰只针对 *最高需量 (MD)* 收费，与太阳能省电费分开计算。',
        bat: (u, k, b) => `${u}x ${k}kWh ${b} 电池储能系统 (BESS)`,
        war: '10年 产品与性能保修',
        om: '1年 O&M 预防性维护（每年检查与维修）',
    } : isMY ? {
        head: '*Cadangan BESS (Peak Shaving)*', size: 'Saiz Bateri', cost: 'Kos Modal', sstL: 'SST (6% atas 20%)', total: 'Jumlah Harga (termasuk SST)',
        save: 'Penjimatan Bulanan', shave: 'Purata MD dikurangkan', s10: 'Jumlah Jimat 10 Tahun', s20: 'Jumlah Jimat 20 Tahun',
        roi: 'ROI', inc: 'Dengan insentif CA & GITA', pkg: 'Pakej Termasuk',
        note: 'Peak shaving mengurangkan caj *Maximum Demand (MD)* sahaja - berasingan daripada penjimatan tenaga solar.',
        bat: (u, k, b) => `${u}x ${k}kWh Sistem Simpanan Tenaga Bateri ${b}`,
        war: 'Waranti Produk dan Prestasi 10 tahun',
        om: 'O&M 1 tahun - Penyelenggaraan Pencegahan (Pemeriksaan & Pembaikan Tahunan)',
    } : {
        head: '*BESS Proposal*', size: 'Battery', cost: 'Capital Cost', sstL: 'SST (6% on 20%)', total: 'Total Price (incl SST)',
        save: 'Monthly Saving', shave: 'Average MD reduction', s10: 'Total 10 Year Saving', s20: 'Total 20 Year Saving',
        roi: 'ROI', inc: 'With CA & GITA incentives', pkg: 'Package Includes',
        note: 'Peak shaving cuts the *Maximum Demand (MD)* charge only - separate from the solar energy saving.',
        bat: (u, k, b) => `${u}x ${k}kWh ${b} Battery Energy Storage System`,
        war: '10-year Product and Performance Warranty',
        om: '1-year O&M Preventive Maintenance (Annual Checking and Repair)',
    };

    // Same shape as the Part A message: header, cost, saving, ROI, then the includes block.
    // A battery carries far fewer line items than a solar system - the product/performance
    // warranty and the O&M year are all there is, so that is all this lists. (user 2026-09-26)
    let m = `${co}${T.head}\n`;
    m += `${fmt(nameplate)}kWh BESS (${pick.units} x ${unitKwh}kWh ${brand})\n`;
    m += `\u26a1 ${T.cost} = ${fmtRM(Math.round(pick.capex || 0))}\n`;
    m += `\u26a1 ${T.sstL} = ${fmtRM2(sst)}\n`;
    m += `\u26a1 *${T.total} = ${fmtRM2((pick.capex || 0) + sst)}*\n\n`;
    m += `\u26a1 *${T.save} = ${fmtRM(Math.round(pick.monthlyAvgSaving || 0))}*\n`;
    m += `${T.shave} ${Math.round(pick.avgShavedKw || 0)} kW\n`;
    m += `\u26a1 *${T.s10} = ${fmtRM(Math.round(cum(10)))}*\n`;
    m += `\u26a1 *${T.s20} = ${fmtRM(Math.round(cum(20)))}*\n\n`;
    if (Number.isFinite(pick.paybackYears)) m += `\u26a1 *${T.roi} = ${pick.paybackYears.toFixed(1)} year*\n`;
    if (Number.isFinite(pick.paybackWithCAGita)) m += `\n${T.inc}\n\u26a1 *${T.roi} = ${pick.paybackWithCAGita.toFixed(1)} year*\n`;
    m += `\n--------------------------------\n\ud83d\udcbc \u3010 ${T.pkg} \u3011\n`;
    m += `\u2705 ${T.bat(pick.units, unitKwh, brand)}\n`;
    m += `\u2705 ${T.war}\n`;
    m += `\u2705 ${T.om}\n`;
    m += `\n${T.note}\n`;
    return m;
}

// Part A + Part B in one message: the solar proposal, a rule, then the BESS block, and a
// combined total so the customer sees ONE number for the whole package.
export function buildCscSolarPlusBessMsg(solarMsg, pick, o = {}, lang = 'en') {
    if (!solarMsg) return buildCscBessMsg(pick, o, lang);
    if (!pick) return solarMsg;
    const isCN = lang === 'zh', isMY = lang === 'ms';
    const T = isCN ? { a: '*A 部分 — 太阳能系统*', b: '*B 部分 — BESS 削峰*', tot: '*A + B 合计*', cap: '总投资 (含 SST)', save: '每月总省' }
        : isMY ? { a: '*Bahagian A — Sistem Solar*', b: '*Bahagian B — BESS Peak Shaving*', tot: '*Jumlah A + B*', cap: 'Jumlah Pelaburan (termasuk SST)', save: 'Jumlah Jimat sebulan' }
        : { a: '*Part A — Solar System*', b: '*Part B — BESS Peak Shaving*', tot: '*Part A + B Total*', cap: 'Total Investment (incl SST)', save: 'Total Monthly Saving' };

    const solarTotal = Number(o.solarTotalInclSst) || 0;
    const solarSaving = Number(o.solarMonthlySaving) || 0;
    const bessTotal = (pick.capex || 0) + sstOn(pick.capex || 0);
    const bessSaving = pick.monthlyAvgSaving || 0;

    let m = `${T.a}\n${solarMsg.trim()}\n`;
    m += `\n════════════════\n${T.b}\n${buildCscBessMsg(pick, { ...o, company: '' }, lang).trim()}\n`;
    if (solarTotal > 0) {
        m += `\n════════════════\n${T.tot}\n`;
        m += `⚡ *${T.cap} = ${fmtRM2(solarTotal + bessTotal)}*\n`;
        m += `⚡ *${T.save} = ${fmtRM(Math.round(solarSaving + bessSaving))}*\n`;
    }
    return m;
}
